import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { Plus, Trash2, Copy, ChevronUp, ChevronDown, X, Bookmark, Search, CalendarIcon, UserPlus, Library, CalendarPlus, FileText } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { api } from "../../lib/api";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import JaceLayout from "./JaceLayout";
import { JACE, TYPE_LABELS, timeStatusColor, uid, isoDay } from "./shared";

const EMPTY_SESSION = {
  title: "", type: "", date: "", start_time: "", planned_duration: 60, location: "Rhöneweg 38",
  level: "BEGINNER", assigned_coach: "", athlete: "", group: "", athlete_id: "", group_id: "", session_format: "", primary_objective: "",
  coach_notes: "", status: "DRAFT", objectives: [], blocks: [],
};

const DEFAULT_DURATION = { BXG: 90, BPT: 60, BG: 60, SC: 60, YTH: 60 };
const DEFAULT_START_TIME = "18:30";

/* Module-scope velden (niet inline) — anders verliezen inputs bij elke toets de focus. */
function JaceTimeField({ value, onChange, testid }) {
  // Standaard 18:30 als doorzichtige placeholder; typen of pijltjes (±30 min)
  const step = (dir) => {
    const base = value || DEFAULT_START_TIME;
    const [h, m] = base.split(":").map((x) => parseInt(x) || 0);
    const total = ((h * 60 + m) + dir * 30 + 1440) % 1440;
    onChange(`${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`);
  };
  return (
    <div className="flex items-center gap-1.5">
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={DEFAULT_START_TIME}
        className="editorial-input dark flex-1 placeholder:text-[#F5F3F0]/25" inputMode="numeric" data-testid={testid} />
      <button type="button" onClick={() => step(1)} className="w-9 h-9 border border-[#F5F3F0]/20 flex items-center justify-center text-[#A3A3A3] hover:text-[#C6FF3C] hover:border-[#C6FF3C] transition-colors" title="+30 min" data-testid={`${testid}-up`}><ChevronUp size={13}/></button>
      <button type="button" onClick={() => step(-1)} className="w-9 h-9 border border-[#F5F3F0]/20 flex items-center justify-center text-[#A3A3A3] hover:text-[#C6FF3C] hover:border-[#C6FF3C] transition-colors" title="-30 min" data-testid={`${testid}-down`}><ChevronDown size={13}/></button>
    </div>
  );
}

function JaceLocationField({ value, onChange, locations, onRefresh, testid }) {
  const [adding, setAdding] = useState(false);
  const [newLoc, setNewLoc] = useState("");
  const add = async () => {
    const name = newLoc.trim();
    if (!name) return;
    try {
      await api.post("/jace/locations", { name });
      setNewLoc(""); setAdding(false);
      await onRefresh();
      onChange(name);
      toast.success(`Locatie "${name}" toegevoegd`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Toevoegen mislukt"); }
  };
  const remove = async () => {
    const current = locations.find((l) => l.name === value);
    if (!current) return;
    if (!window.confirm(`Locatie "${current.name}" verwijderen uit de lijst?`)) return;
    try {
      await api.delete(`/jace/locations/${current.id}`);
      await onRefresh();
      onChange("Rhöneweg 38");
      toast.success("Locatie verwijderd");
    } catch { toast.error("Verwijderen mislukt"); }
  };
  return (
    <div>
      <div className="flex gap-1.5">
        <select value={locations.some((l) => l.name === value) ? value : ""} onChange={(e) => e.target.value && onChange(e.target.value)}
          className="editorial-input dark flex-1" data-testid={`${testid}-select`}>
          <option value="" className="bg-[#0A0A0A]">Kies locatie…</option>
          {locations.map((l) => <option key={l.id} value={l.name} className="bg-[#0A0A0A]">{l.name}</option>)}
        </select>
        <button type="button" onClick={() => setAdding(!adding)} className="w-9 h-9 border border-[#F5F3F0]/25 flex items-center justify-center text-[#A3A3A3] hover:border-[#C6FF3C] hover:text-[#C6FF3C] transition-colors shrink-0" title="Nieuwe locatie" data-testid={`${testid}-add`}><Plus size={14}/></button>
        <button type="button" onClick={remove} className="w-9 h-9 border border-[#F5F3F0]/25 flex items-center justify-center text-[#A3A3A3] hover:text-red-400 transition-colors shrink-0" title="Locatie verwijderen" data-testid={`${testid}-del`}><Trash2 size={13}/></button>
      </div>
      {!locations.some((l) => l.name === value) && value && (
        <div className="chip text-[#4A4A4A] mt-1">Vrije invoer: {value}</div>
      )}
      {adding && (
        <div className="flex gap-2 mt-2" data-testid={`${testid}-add-form`}>
          <input autoFocus value={newLoc} onChange={(e) => setNewLoc(e.target.value)} placeholder="Naam nieuwe locatie" className="editorial-input dark flex-1" data-testid={`${testid}-new`}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} />
          <button type="button" onClick={add} className="chip px-4 py-2 border" style={{ borderColor: JACE, color: JACE }} data-testid={`${testid}-save`}>Toevoegen</button>
        </div>
      )}
      <input value={value} onChange={(e) => onChange(e.target.value)} className="editorial-input dark mt-2" placeholder="Of typ een andere locatie…" data-testid={testid} />
    </div>
  );
}

const SectionTitle = ({ num, children }) => (
  <div className="chip text-[#A3A3A3] mb-4 mt-10 first:mt-0">SECTION {num} <span style={{ color: JACE }}>// {children}</span></div>
);

export default function JaceSessionBuilder() {
  const nav = useNavigate();
  const { sessionId } = useParams();
  const [params] = useSearchParams();
  const isNew = !sessionId;

  const [meta, setMeta] = useState(null);
  const [s, setS] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [members, setMembers] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [jaceGroups, setJaceGroups] = useState([]);
  const [me, setMe] = useState("");
  const [addCoach, setAddCoach] = useState(false);
  const [newCoach, setNewCoach] = useState("");
  const [calOpen, setCalOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState(null);
  const [locations, setLocations] = useState([]);
  // Coaching Engine: ?engine=<id> bewerkt een bibliotheek-item i.p.v. een sessie
  const engineId = params.get("engine");
  const [engineItem, setEngineItem] = useState(null); // geladen bibliotheek-item (engine-modus)
  const [enginePicker, setEnginePicker] = useState(false);
  const [engineChoice, setEngineChoice] = useState(null); // gekozen item → replace/append-keuze

  const loadLocations = () => api.get("/jace/locations").then(({ data }) => setLocations(data.items || [])).catch(() => {});

  useEffect(() => {
    api.get("/jace/meta").then(({ data }) => setMeta(data)).catch(() => {});
    api.get("/jace/templates").then(({ data }) => setTemplates(data.items || [])).catch(() => {});
    api.get("/jace/coaches").then(({ data }) => setCoaches(data.items || [])).catch(() => {});
    api.get("/admin/members").then(({ data }) => setMembers((data.items || []).filter((m) => m.name)).sort((a, b) => a.name.localeCompare(b.name))).catch(() => {});
    api.get("/jace/athletes").then(({ data }) => setAthletes(data.items || [])).catch(() => {});
    api.get("/jace/groups").then(({ data }) => setJaceGroups(data.items || [])).catch(() => {});
    loadLocations();
    const token = localStorage.getItem("joh_admin_token");
    api.get("/auth/me", { headers: { Authorization: `Bearer ${token}` } }).then(({ data }) => setMe(data.name || data.email)).catch(() => {});
  }, []);

  // Sessie laden / initialiseren zodra "me" bekend is (default coach = ingelogde coach)
  useEffect(() => {
    if (engineId) {
      // Engine-modus: bibliotheek-item laden (snapshot, vrij aanpasbaar)
      api.get(`/jace/library/${engineId}`).then(({ data }) => {
        setEngineItem(data);
        setS({ ...EMPTY_SESSION, title: data.name, type: data.type || "BXG",
          level: data.level || "BEGINNER", planned_duration: data.planned_duration || 60,
          date: params.get("date") || isoDay(new Date()), assigned_coach: me || "",
          objectives: (data.objectives || []).map((o) => ({ ...o })),
          blocks: (data.blocks || []).map((b) => ({ ...b, id: uid(), exercises: (b.exercises || []).map((e) => ({ ...e, id: uid() })) })) });
      }).catch(() => setS(false));
      return;
    }
    if (isNew) {
      const type = params.get("type") || "BXG";
      setS({ ...EMPTY_SESSION, type, date: params.get("date") || isoDay(new Date()),
        planned_duration: DEFAULT_DURATION[type] || 60, assigned_coach: me || "" });
    } else {
      api.get(`/jace/sessions/${sessionId}`).then(({ data }) => setS({ ...EMPTY_SESSION, ...data })).catch(() => setS(false));
    }
  }, [sessionId, me, engineId]); // eslint-disable-line

  // Template direct laden via ?template=<id> (vanuit templates-tabblad)
  useEffect(() => {
    const tid = params.get("template");
    if (isNew && tid && templates.length > 0 && s && !s.template_id && s.blocks.length === 0) loadTemplate(tid);
  }, [templates, s]); // eslint-disable-line

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const changeType = (t) => setS((p) => ({ ...p, type: t, planned_duration: DEFAULT_DURATION[t] || 60, session_format: "", athlete: "", group: "", athlete_id: "", group_id: "" }));
  const setBlock = (i, patch) => setS((p) => ({ ...p, blocks: p.blocks.map((b, j) => (j === i ? { ...b, ...patch } : b)) }));
  const moveBlock = (i, dir) => setS((p) => {
    const blocks = [...p.blocks];
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return p;
    [blocks[i], blocks[j]] = [blocks[j], blocks[i]];
    return { ...p, blocks };
  });
  const addBlock = (name) => setS((p) => ({ ...p, blocks: [...p.blocks, { id: uid(), name: name || "CUSTOM", duration: 0, coach_note: "", exercises: [] }] }));

  const totalPlanned = useMemo(() => (s?.blocks || []).reduce((t, b) => t + (b.duration || b.exercises.reduce((x, e) => x + (e.duration || 0), 0) || 0), 0), [s]);
  const timeStatus = totalPlanned === (s?.planned_duration || 0) ? "ON TARGET" : totalPlanned > (s?.planned_duration || 0) ? "OVER TIME" : "UNDER TIME";

  const saveCoach = async () => {
    const name = newCoach.trim();
    if (!name) return;
    try {
      await api.post("/jace/coaches", { name });
      const { data } = await api.get("/jace/coaches");
      setCoaches(data.items || []);
      set("assigned_coach", name);
      setAddCoach(false);
      setNewCoach("");
      toast.success(`Coach "${name}" toegevoegd`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Coach toevoegen mislukt"); }
  };

  const save = async () => {
    if (!s.title.trim()) { toast.error("Titel is verplicht"); return; }
    setBusy(true);
    const payload = { ...s, planned_duration: parseInt(s.planned_duration) || 0, start_time: s.start_time || DEFAULT_START_TIME };
    try {
      if (engineId) {
        // Engine-modus: schrijf terug naar de bibliotheek (maakt automatisch een versie-snapshot)
        await api.put(`/jace/library/${engineId}`, {
          name: s.title, type: s.type, level: s.level,
          planned_duration: payload.planned_duration, objectives: s.objectives, blocks: s.blocks,
        });
        toast.success("Opgeslagen in Coaching Engine");
        nav("/jace/engine");
        return;
      }
      if (isNew) {
        const { data } = await api.post("/jace/sessions", payload);
        toast.success(`Sessie ${data.session.session_id} aangemaakt`);
        nav(`/jace/sessions/${data.session.session_id}`);
      } else {
        await api.put(`/jace/sessions/${sessionId}`, payload);
        toast.success("Sessie opgeslagen");
        nav(`/jace/sessions/${sessionId}`);
      }
    } catch (err) { toast.error(err?.response?.data?.detail || "Opslaan mislukt"); }
    setBusy(false);
  };

  // Engine-modus: huidige inhoud als NIEUWE bibliotheek-training opslaan
  const saveAsNewEngineItem = async () => {
    if (!s.title.trim()) { toast.error("Titel is verplicht"); return; }
    if (!window.confirm(`"${s.title}" als nieuwe training in de Engine opslaan? Het origineel blijft ongewijzigd.`)) return;
    setBusy(true);
    try {
      await api.post("/jace/library", {
        name: s.title, type: s.type, level: s.level,
        planned_duration: parseInt(s.planned_duration) || 60,
        objectives: s.objectives, blocks: s.blocks,
        folder: engineItem?.folder || "", color: engineItem?.color, tags: engineItem?.tags || [],
      });
      toast.success("Nieuwe training opgeslagen in de Engine");
      nav("/jace/engine");
    } catch (err) { toast.error(err?.response?.data?.detail || "Opslaan mislukt"); }
    setBusy(false);
  };

  // Engine-modus: plan de huidige inhoud als echte sessie (draft)
  const planAsSession = async () => {
    if (!s.title.trim()) { toast.error("Titel is verplicht"); return; }
    setBusy(true);
    try {
      const payload = { ...s, planned_duration: parseInt(s.planned_duration) || 0, start_time: s.start_time || DEFAULT_START_TIME, status: "DRAFT" };
      const { data } = await api.post("/jace/sessions", payload);
      api.post(`/jace/library/${engineId}/use`).catch(() => {});
      toast.success(`Sessie ${data.session.session_id} gepland als concept`);
      nav(`/jace/sessions/${data.session.session_id}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Plannen mislukt"); }
    setBusy(false);
  };

  // Bestaande sessie bewaren als Engine-training
  const saveSessionToEngine = async () => {
    if (!sessionId) return;
    setBusy(true);
    try {
      await api.post(`/jace/library/from-session/${sessionId}`, {});
      toast.success("Sessie bewaard in de Coaching Engine");
    } catch (err) { toast.error(err?.response?.data?.detail || "Bewaren mislukt"); }
    setBusy(false);
  };

  // Import uit de Engine — snapshot: vervang alles of voeg toe aan de huidige sessie.
  // NB: de lijst-endpoint levert geen blocks/objectives (perf) → volledige item ophalen.
  const applyEngineItem = async (item, mode) => {
    let full = item;
    try {
      const { data } = await api.get(`/jace/library/${item.id}`);
      full = data;
    } catch { toast.error("Kon training niet laden"); return; }
    const blocks = (full.blocks || []).map((b) => ({ ...b, id: uid(), exercises: (b.exercises || []).map((e) => ({ ...e, id: uid() })) }));
    const objectives = (full.objectives || []).map((o) => ({ ...o }));
    setS((p) => mode === "replace"
      ? { ...p, title: full.name, type: full.type || p.type, level: full.level || p.level,
          planned_duration: full.planned_duration || p.planned_duration, objectives, blocks }
      : { ...p, objectives: [...(p.objectives || []), ...objectives], blocks: [...(p.blocks || []), ...blocks] });
    api.post(`/jace/library/${item.id}/use`).catch(() => {});
    toast.success(`"${full.name}" ${mode === "replace" ? "geladen" : "toegevoegd"} — snapshot, vrij aanpasbaar`);
    setEnginePicker(false);
    setEngineChoice(null);
  };

  const loadTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    setS((p) => ({ ...p, title: t.name, type: t.type, level: t.level || p.level, planned_duration: t.planned_duration || DEFAULT_DURATION[t.type] || 60,
      objectives: (t.objectives || []).map((o) => ({ ...o })), blocks: (t.blocks || []).map((b) => ({ ...b, id: uid(), exercises: (b.exercises || []).map((e) => ({ ...e, id: uid() })) })),
      template_id: t.id, template_version: t.version }));
    toast.success(`Template "${t.name}" geladen — velden blijven aanpasbaar`);
  };

  if (s === null || !meta) return <JaceLayout><div className="chip text-[#A3A3A3]">Laden…</div></JaceLayout>;
  if (s === false) return <JaceLayout><div className="border border-[#F5F3F0]/15 p-10 text-center chip text-[#4A4A4A]">Sessie niet gevonden.</div></JaceLayout>;

  const blockOptions = meta.blocks[s.type] || [];
  const showLinking = s.type !== "BXG"; // Boxing Group: geen athlete/group in standaard flow
  const dateObj = s.date ? new Date(s.date + "T00:00:00") : undefined;

  return (
    <JaceLayout>
      <div className="max-w-5xl" data-testid="jace-builder">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="section-tag section-tag-dark mb-3">{engineId ? "// COACHING ENGINE" : "// SESSION BUILDER"}</div>
            <h2 className="font-display uppercase text-4xl md:text-5xl">{engineId ? `Engine: ${s.title || "…"}` : isNew ? "New session." : `Edit ${s.session_id}.`}</h2>
            {engineId && <p className="chip text-[#A3A3A3] mt-2">Je bewerkt een bibliotheek-training — geen datum, geen rooster. Opslaan maakt automatisch een nieuwe versie.</p>}
          </div>
          <div className="flex items-center gap-3">
            {!engineId && (
              <button onClick={() => setEnginePicker(true)} className="chip px-4 py-2.5 border inline-flex items-center gap-2 transition-colors hover:text-[#C6FF3C]"
                style={{ borderColor: JACE, color: JACE }} title="Importeer een training uit de Coaching Engine" data-testid="jace-engine-import-btn">
                <Library size={13} /> ENGINE
              </button>
            )}
            {!engineId && ["COMPLETED", "REVIEWED"].includes(s.status) && (
              <Link to={`/jace/feedback/new?session=${s.session_id}`} className="chip px-4 py-2.5 border inline-flex items-center gap-2 transition-colors hover:text-[#C6FF3C]"
                style={{ borderColor: JACE, color: JACE }} title="Feedback toevoegen" data-testid="jace-builder-feedback-btn">
                <FileText size={13} /> FEEDBACK
              </Link>
            )}
            <div className="chip px-3 py-2 border" style={{ color: timeStatusColor(timeStatus), borderColor: `${timeStatusColor(timeStatus)}55` }} data-testid="jace-builder-time">
              {totalPlanned} / {s.planned_duration || 0} MIN · {timeStatus}
            </div>
          </div>
        </div>

        {/* Template kiezen bij nieuwe sessie */}
        {isNew && templates.filter((t) => !t.archived).length > 0 && s.blocks.length === 0 && (
          <div className="border border-[#F5F3F0]/15 p-4 mb-2 flex flex-wrap items-center gap-3" data-testid="jace-template-picker">
            <span className="chip text-[#A3A3A3]">START VANAF NUL — OF GEBRUIK TEMPLATE:</span>
            {templates.filter((t) => !t.archived).map((t) => (
              <button key={t.id} onClick={() => loadTemplate(t.id)} className="chip px-3 py-2 border border-[#F5F3F0]/25 hover:border-[#C6FF3C] hover:text-[#C6FF3C] transition-colors inline-flex items-center gap-2" data-testid={`jace-template-${t.id}`}>
                <Bookmark size={11}/> {t.name} {t.theme ? <span className="text-[#4A4A4A]">· {t.theme}</span> : null} <span className="text-[#4A4A4A]">{t.version}</span>
              </button>
            ))}
          </div>
        )}

        {/* SECTION 01 — BASICS */}
        <SectionTitle num="01">SESSION BASICS</SectionTitle>
        <div className="grid grid-cols-2 gap-4" data-testid="jace-builder-info">
          <div className="col-span-2"><label className="chip text-[#A3A3A3]">Session title*</label><input value={s.title} onChange={(e) => set("title", e.target.value)} className="editorial-input dark" data-testid="jace-f-title" /></div>
          <div><label className="chip text-[#A3A3A3]">Type</label>
            <select value={s.type} onChange={(e) => changeType(e.target.value)} className="editorial-input dark" data-testid="jace-f-type">
              {Object.entries(TYPE_LABELS).map(([k, l]) => <option key={k} value={k} className="bg-[#0A0A0A]">{l}</option>)}
            </select>
          </div>
          <div><label className="chip text-[#A3A3A3]">Level</label>
            <select value={s.level} onChange={(e) => set("level", e.target.value)} className="editorial-input dark" data-testid="jace-f-level">
              {meta.levels.map((l) => <option key={l} value={l} className="bg-[#0A0A0A]">{l}</option>)}
            </select>
          </div>
          <div>
            <label className="chip text-[#A3A3A3]">Datum</label>
            <Popover open={calOpen} onOpenChange={setCalOpen}>
              <PopoverTrigger asChild>
                <button type="button" className="editorial-input dark w-full text-left inline-flex items-center gap-2" data-testid="jace-f-date">
                  <CalendarIcon size={13} className="text-[#4A4A4A]" />
                  {dateObj ? format(dateObj, "dd/MM/yyyy") : "Kies datum"}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-[#0A0A0A] border border-[#F5F3F0]/20 rounded-none" align="start">
                <Calendar mode="single" selected={dateObj} onSelect={(d) => { if (d) set("date", isoDay(d)); setCalOpen(false); }}
                  weekStartsOn={1} locale={nl} defaultMonth={dateObj || new Date()} data-testid="jace-f-calendar"
                  classNames={{
                    caption_label: "text-sm font-medium text-[#F5F3F0]",
                    nav_button: "h-7 w-7 bg-transparent p-0 opacity-70 hover:opacity-100 text-[#DDD6CB] border border-[#F5F3F0]/20",
                    head_cell: "text-[#4A4A4A] rounded-md w-8 font-normal text-[0.8rem]",
                    day: "h-8 w-8 p-0 font-normal text-[#DDD6CB] hover:bg-[#F5F3F0]/10 rounded-none",
                    day_selected: "bg-[#C6FF3C] text-[#0A0A0A] hover:bg-[#C6FF3C] hover:text-[#0A0A0A]",
                    day_today: "text-[#C6FF3C] font-bold",
                    day_outside: "text-[#4A4A4A] opacity-60",
                    day_disabled: "text-[#4A4A4A] opacity-40",
                  }} />
              </PopoverContent>
            </Popover>
          </div>
          <div><label className="chip text-[#A3A3A3]">Starttijd (standaard {DEFAULT_START_TIME})</label><JaceTimeField value={s.start_time} onChange={(v) => set("start_time", v)} testid="jace-f-start" /></div>
          <div><label className="chip text-[#A3A3A3]">Geplande duur (min) — default {DEFAULT_DURATION[s.type]}</label><input type="number" min="0" value={s.planned_duration} onChange={(e) => set("planned_duration", e.target.value)} className="editorial-input dark" data-testid="jace-f-duration" /></div>
          <div className="col-span-2 md:col-span-1"><label className="chip text-[#A3A3A3]">Locatie</label><JaceLocationField value={s.location} onChange={(v) => set("location", v)} locations={locations} onRefresh={loadLocations} testid="jace-f-location" /></div>
          <div className="col-span-2 md:col-span-1">
            <label className="chip text-[#A3A3A3]">Assigned coach</label>
            <div className="flex gap-2">
              <select value={s.assigned_coach} onChange={(e) => set("assigned_coach", e.target.value)} className="editorial-input dark flex-1" data-testid="jace-f-coach">
                <option value="" className="bg-[#0A0A0A]">COACH</option>
                {coaches.map((c) => <option key={c.id} value={c.name} className="bg-[#0A0A0A]">{c.name}</option>)}
              </select>
              <button type="button" onClick={() => setAddCoach(!addCoach)} className="w-11 h-11 border border-[#F5F3F0]/25 flex items-center justify-center text-[#A3A3A3] hover:border-[#C6FF3C] hover:text-[#C6FF3C] transition-colors shrink-0" title="+ Add coach" data-testid="jace-add-coach-btn"><UserPlus size={15}/></button>
            </div>
            {addCoach && (
              <div className="flex gap-2 mt-2" data-testid="jace-add-coach-form">
                <input autoFocus value={newCoach} onChange={(e) => setNewCoach(e.target.value)} placeholder="Naam nieuwe coach" className="editorial-input dark flex-1" data-testid="jace-new-coach-name"
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), saveCoach())} />
                <button type="button" onClick={saveCoach} className="chip px-4 py-2 border" style={{ borderColor: JACE, color: JACE }} data-testid="jace-save-coach-btn">Toevoegen</button>
              </div>
            )}
          </div>
        </div>

        {/* SECTION 02 — TARGET */}
        <SectionTitle num="02">SESSION TARGET</SectionTitle>
        <div data-testid="jace-builder-objectives">
          <div className="mb-4"><label className="chip text-[#A3A3A3]">Primary objective</label><input value={s.primary_objective} onChange={(e) => set("primary_objective", e.target.value)} className="editorial-input dark" data-testid="jace-f-objective" /></div>
          <div className="flex items-center justify-between mb-2">
            <span className="chip text-[#A3A3A3]">Session objectives (1–3)</span>
            {s.objectives.length < 3 && (
              <button onClick={() => set("objectives", [...s.objectives, { title: "", description: "" }])} className="chip border border-[#F5F3F0]/25 px-3 py-1.5 hover:bg-[#F5F3F0] hover:text-[#0A0A0A] transition-colors inline-flex items-center gap-1" data-testid="jace-add-objective"><Plus size={12}/> Objective</button>
            )}
          </div>
          {s.objectives.map((o, i) => (
            <div key={i} className="flex items-center gap-3 mb-2">
              <span className="chip shrink-0" style={{ color: JACE }}>{String(i + 1).padStart(2, "0")}</span>
              <input value={o.title} onChange={(e) => set("objectives", s.objectives.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} className="editorial-input dark" placeholder={`Objective ${i + 1}`} data-testid={`jace-objective-${i}`} />
              <button onClick={() => set("objectives", s.objectives.filter((_, j) => j !== i))} className="text-[#4A4A4A] hover:text-red-400"><X size={14}/></button>
            </div>
          ))}
        </div>

        {/* SECTION 03 — LINKING (conditioneel per type) */}
        {showLinking && (
          <>
            <SectionTitle num="03">SESSION LINKING</SectionTitle>
            <div className="grid grid-cols-2 gap-4" data-testid="jace-builder-linking">
              {s.type === "BPT" ? (
                <>
                  <div className="col-span-2">
                    <label className="chip text-[#A3A3A3]">Session format</label>
                    <div className="flex gap-2 mt-1">
                      {[["ATHLETE", "ATHLETE"], ["SMALL_GROUP", "SMALL GROUP"]].map(([v, l]) => (
                        <button type="button" key={v} onClick={() => set("session_format", v)}
                          className="chip px-4 py-2.5 border transition-colors"
                          style={s.session_format === v ? { borderColor: JACE, color: JACE } : { borderColor: "#F5F3F033", color: "#A3A3A3" }}
                          data-testid={`jace-format-${v}`}>{l}</button>
                      ))}
                    </div>
                  </div>
                  {s.session_format === "ATHLETE" && (
                    <div className="col-span-2"><label className="chip text-[#A3A3A3]">Athlete</label>
                      <select
                        value={s.athlete_id ? `jace:${s.athlete_id}` : s.athlete}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v.startsWith("jace:")) {
                            const a = athletes.find((x) => `jace:${x.id}` === v);
                            setS((p) => ({ ...p, athlete: a ? a.name : "", athlete_id: a ? a.id : "" }));
                          } else {
                            setS((p) => ({ ...p, athlete: v, athlete_id: "" }));
                          }
                        }}
                        className="editorial-input dark" data-testid="jace-f-athlete">
                        <option value="" className="bg-[#0A0A0A]">Kies athlete…</option>
                        {athletes.length > 0 && <option disabled className="bg-[#0A0A0A]">— JACE ATLETEN —</option>}
                        {athletes.map((a) => <option key={a.id} value={`jace:${a.id}`} className="bg-[#0A0A0A]">{a.name}</option>)}
                        {members.length > 0 && <option disabled className="bg-[#0A0A0A]">— WEBSITE LEDEN —</option>}
                        {members.map((m) => <option key={m.email} value={m.name} className="bg-[#0A0A0A]">{m.name}</option>)}
                      </select>
                    </div>
                  )}
                  {s.session_format === "SMALL_GROUP" && (
                    <div className="col-span-2"><label className="chip text-[#A3A3A3]">Small group</label>
                      {jaceGroups.length > 0 && (
                        <select
                          value={s.group_id || ""}
                          onChange={(e) => {
                            const g = jaceGroups.find((x) => x.id === e.target.value);
                            setS((p) => ({ ...p, group: g ? g.name : p.group, group_id: g ? g.id : "" }));
                          }}
                          className="editorial-input dark mb-2" data-testid="jace-f-group-select">
                          <option value="" className="bg-[#0A0A0A]">Kies uit JACE groepen…</option>
                          {jaceGroups.map((g) => <option key={g.id} value={g.id} className="bg-[#0A0A0A]">{g.name} ({(g.athlete_ids || []).length})</option>)}
                        </select>
                      )}
                      <input value={s.group} onChange={(e) => setS((p) => ({ ...p, group: e.target.value, group_id: "" }))} placeholder="bv. Sparring groep A" className="editorial-input dark" data-testid="jace-f-group" />
                    </div>
                  )}
                </>
              ) : (
                <div className="col-span-2 md:col-span-1">
                  <label className="chip text-[#A3A3A3]">Athlete / Group (optioneel)</label>
                  <input value={s.athlete || s.group} onChange={(e) => set(s.type === "YTH" ? "group" : "athlete", e.target.value)} className="editorial-input dark" data-testid="jace-f-athlete" />
                </div>
              )}
            </div>
          </>
        )}

        {/* SECTION 04 — BUILD */}
        <SectionTitle num="04">BUILD SESSION</SectionTitle>
        <div className="mb-4 flex items-center justify-between">
          <span className="chip text-[#A3A3A3]">TRAINING BLOCKS ({s.blocks.length})</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-testid="jace-builder-blocks">
          {s.blocks.map((b, i) => (
            <BlockEditor key={b.id} b={b} i={i} total={s.blocks.length}
              onPatch={(patch) => setBlock(i, patch)}
              onMove={(dir) => moveBlock(i, dir)}
              onDuplicate={() => setS((p) => ({ ...p, blocks: [...p.blocks.slice(0, i + 1), { ...b, id: uid(), exercises: b.exercises.map((e) => ({ ...e, id: uid() })) }, ...p.blocks.slice(i + 1)] }))}
              onDelete={() => setS((p) => ({ ...p, blocks: p.blocks.filter((_, j) => j !== i) }))}
              onPickExercise={() => setPicker(i)}
              blockOptions={blockOptions}
            />
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-8" data-testid="jace-add-block-list">
          {blockOptions.map((name) => (
            <button key={name} onClick={() => addBlock(name)} className="chip px-3 py-2 border border-[#F5F3F0]/20 text-[#A3A3A3] hover:border-[#C6FF3C] hover:text-[#C6FF3C] transition-colors" data-testid={`jace-add-block-${name.replace(/[^A-Z0-9]/gi, "-")}`}>
              + {name}
            </button>
          ))}
        </div>

        <div className="mb-10"><label className="chip text-[#A3A3A3]">General coach notes</label><textarea rows={3} value={s.coach_notes} onChange={(e) => set("coach_notes", e.target.value)} className="editorial-input dark w-full" data-testid="jace-f-notes" /></div>

        <div className="flex flex-wrap gap-3">
          {engineId ? (
            <>
              <button onClick={save} disabled={busy} className="px-8 py-3.5 chip" style={{ background: JACE, color: "#0A0A0A" }} data-testid="jace-save-btn">{busy ? "Opslaan…" : "SAVE TO ENGINE"}</button>
              <button onClick={planAsSession} disabled={busy} className="chip px-5 py-3.5 border border-[#F5F3F0]/25 text-[#F5F3F0] hover:border-[#C6FF3C] hover:text-[#C6FF3C] transition-colors inline-flex items-center gap-2" data-testid="jace-engine-plan-btn">
                <CalendarPlus size={13} /> PLAN ALS SESSIE
              </button>
              <button onClick={saveAsNewEngineItem} disabled={busy} className="chip px-5 py-3.5 border border-[#F5F3F0]/25 text-[#A3A3A3] hover:text-[#F5F3F0] transition-colors inline-flex items-center gap-2" data-testid="jace-engine-saveas-btn">
                <Copy size={13} /> ALS NIEUW OPSLAAN
              </button>
              <Link to="/jace/engine" className="btn-ghost-dark" data-testid="jace-cancel-btn">Terug naar Engine</Link>
            </>
          ) : (
            <>
              <button onClick={save} disabled={busy} className="px-8 py-3.5 chip" style={{ background: JACE, color: "#0A0A0A" }} data-testid="jace-save-btn">{busy ? "Opslaan…" : "SAVE SESSION"}</button>
              {!isNew && (
                <button onClick={saveSessionToEngine} disabled={busy} className="chip px-5 py-3.5 border border-[#F5F3F0]/25 text-[#A3A3A3] hover:border-[#C6FF3C] hover:text-[#C6FF3C] transition-colors inline-flex items-center gap-2" title="Bewaar deze sessie als herbruikbare training" data-testid="jace-save-to-engine-btn">
                  <Library size={13} /> NAAR ENGINE
                </button>
              )}
              <Link to={isNew ? "/jace/sessions" : `/jace/sessions/${sessionId}`} className="btn-ghost-dark" data-testid="jace-cancel-btn">Annuleren</Link>
            </>
          )}
        </div>
      </div>

      {enginePicker && (
        <EnginePicker
          onClose={() => { setEnginePicker(false); setEngineChoice(null); }}
          choice={engineChoice}
          onSelect={(item) => {
            if (!item) { setEngineChoice(null); return; }
            if ((s.blocks || []).length > 0 || (s.objectives || []).length > 0) setEngineChoice(item);
            else applyEngineItem(item, "replace");
          }}
          onApply={applyEngineItem}
        />
      )}

      {picker !== null && (
        <ExercisePicker type={s.type} onClose={() => setPicker(null)}
          onPick={(ex) => {
            setBlock(picker, { exercises: [...s.blocks[picker].exercises, { id: uid(), exercise_id: ex.id, name: ex.name, duration: ex.duration || 0, coach_note: "", detail: "" }] });
            setPicker(null);
          }}
          onCustom={(name) => {
            setBlock(picker, { exercises: [...s.blocks[picker].exercises, { id: uid(), exercise_id: "", name, duration: 5, coach_note: "", detail: "" }] });
            setPicker(null);
          }} />
      )}
    </JaceLayout>
  );
}

function BlockEditor({ b, i, total, onPatch, onMove, onDuplicate, onDelete, onPickExercise, blockOptions }) {
  return (
    <div className="border border-[#F5F3F0]/15 p-4 flex flex-col" data-testid={`jace-block-${i}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="chip shrink-0" style={{ color: JACE }}>{String(i + 1).padStart(2, "0")}</span>
        <select value={blockOptions.includes(b.name) ? b.name : "CUSTOM"} onChange={(e) => onPatch({ name: e.target.value })} className="editorial-input dark !w-auto flex-1 min-w-28" data-testid={`jace-block-name-${i}`}>
          {blockOptions.map((n) => <option key={n} value={n} className="bg-[#0A0A0A]">{n}</option>)}
        </select>
        <input type="number" min="0" value={b.duration} onChange={(e) => onPatch({ duration: parseInt(e.target.value) || 0 })} className="editorial-input dark !w-16" title="Duur (min)" data-testid={`jace-block-dur-${i}`} />
        <span className="chip text-[#4A4A4A]">MIN</span>
      </div>
      <div className="flex gap-1 mt-2 justify-end">
        <button onClick={() => onMove(-1)} disabled={i === 0} className="w-8 h-8 border border-[#F5F3F0]/20 flex items-center justify-center text-[#A3A3A3] hover:text-[#F5F3F0] disabled:opacity-30" title="Omhoog" data-testid={`jace-block-up-${i}`}><ChevronUp size={13}/></button>
        <button onClick={() => onMove(1)} disabled={i === total - 1} className="w-8 h-8 border border-[#F5F3F0]/20 flex items-center justify-center text-[#A3A3A3] hover:text-[#F5F3F0] disabled:opacity-30" title="Omlaag" data-testid={`jace-block-down-${i}`}><ChevronDown size={13}/></button>
        <button onClick={onDuplicate} className="w-8 h-8 border border-[#F5F3F0]/20 flex items-center justify-center text-[#A3A3A3] hover:text-[#F5F3F0]" title="Dupliceer" data-testid={`jace-block-dup-${i}`}><Copy size={13}/></button>
        <button onClick={onDelete} className="w-8 h-8 border border-[#F5F3F0]/20 flex items-center justify-center text-[#A3A3A3] hover:text-red-400" title="Verwijder" data-testid={`jace-block-del-${i}`}><Trash2 size={13}/></button>
      </div>
      <input value={b.coach_note} onChange={(e) => onPatch({ coach_note: e.target.value })} placeholder="COACH NOTE // bv. Technique before speed." className="editorial-input dark mt-2 !text-[#D4A93A]" data-testid={`jace-block-note-${i}`} />
      <div className="flex-1">
        {(b.exercises || []).map((ex, j) => (
          <div key={ex.id} className="flex items-center gap-2 mt-2" data-testid={`jace-block-ex-${i}-${j}`}>
            <span className="flex flex-col shrink-0">
              <button type="button" disabled={j === 0} onClick={() => onPatch({ exercises: b.exercises.map((x, k) => k === j ? b.exercises[j - 1] : k === j - 1 ? b.exercises[j] : x) })}
                className="text-[#4A4A4A] hover:text-[#C6FF3C] disabled:opacity-20" title="Omhoog" data-testid={`jace-ex-up-${i}-${j}`}><ChevronUp size={12}/></button>
              <button type="button" disabled={j === b.exercises.length - 1} onClick={() => onPatch({ exercises: b.exercises.map((x, k) => k === j ? b.exercises[j + 1] : k === j + 1 ? b.exercises[j] : x) })}
                className="text-[#4A4A4A] hover:text-[#C6FF3C] disabled:opacity-20" title="Omlaag" data-testid={`jace-ex-down-${i}-${j}`}><ChevronDown size={12}/></button>
            </span>
            <input value={ex.name} onChange={(e) => onPatch({ exercises: b.exercises.map((x, k) => (k === j ? { ...x, name: e.target.value } : x)) })} className="editorial-input dark flex-1" />
            <input type="number" min="0" value={ex.duration} onChange={(e) => onPatch({ exercises: b.exercises.map((x, k) => (k === j ? { ...x, duration: parseInt(e.target.value) || 0 } : x)) })} className="editorial-input dark !w-16" title="Min" />
            <button onClick={() => onPatch({ exercises: b.exercises.filter((_, k) => k !== j) })} className="text-[#4A4A4A] hover:text-red-400" data-testid={`jace-ex-del-${i}-${j}`}><X size={13}/></button>
          </div>
        ))}
      </div>
      <button onClick={onPickExercise} className="chip mt-3 inline-flex items-center gap-1.5 border border-[#F5F3F0]/20 px-3 py-1.5 text-[#A3A3A3] hover:border-[#C6FF3C] hover:text-[#C6FF3C] transition-colors w-fit" data-testid={`jace-block-addex-${i}`}>
        <Plus size={11}/> EXERCISE UIT LIBRARY
      </button>
    </div>
  );
}

function ExercisePicker({ type, onClose, onPick, onCustom }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/jace/exercises", { params: { q, type } }).then(({ data }) => setItems(data.items)).catch(() => setItems([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q, type]);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-[#F5F3F0]/20 max-w-xl w-full p-6 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="jace-exercise-picker">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display uppercase text-2xl">Kies exercise.</div>
          <button onClick={onClose} className="w-8 h-8 border border-[#F5F3F0]/25 flex items-center justify-center"><X size={14}/></button>
        </div>
        <div className="relative mb-3">
          <Search size={13} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#4A4A4A]" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek in library…" className="editorial-input dark !pl-5" data-testid="jace-picker-search" />
        </div>
        {q.trim() && (
          <button onClick={() => onCustom(q.trim())} className="chip mb-3 px-3 py-2 border border-dashed border-[#F5F3F0]/30 text-[#A3A3A3] hover:text-[#C6FF3C] hover:border-[#C6FF3C] transition-colors text-left" data-testid="jace-picker-custom">
            + Voeg "{q.trim()}" toe als vrije activiteit
          </button>
        )}
        <div className="overflow-y-auto line-t line-dark flex-1" data-testid="jace-picker-list">
          {items === null ? <div className="chip text-[#A3A3A3] py-4">Laden…</div>
            : items.length === 0 ? <div className="chip text-[#4A4A4A] py-4">Geen oefeningen gevonden.</div>
            : items.map((ex) => (
              <button key={ex.id} onClick={() => onPick(ex)} className="w-full text-left line-b line-dark py-3 flex items-center gap-3 hover:bg-white/[0.03] transition-colors" data-testid={`jace-picker-${ex.id}`}>
                <span className="flex-1 text-sm text-[#DDD6CB]">{ex.name}</span>
                <span className="chip text-[#4A4A4A]">{ex.category}</span>
                <span className="chip text-[#4A4A4A]">{ex.duration || 0} MIN</span>
              </button>
            ))}
        </div>
      </div>
    </div>
  );
}


/* Engine-picker in de Session Builder: kies een training uit de bibliotheek.
   Bij bestaande inhoud eerst de keuze: vervangen (met waarschuwing) of toevoegen. */
function EnginePicker({ onClose, choice, onSelect, onApply }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/jace/library", { params: { q, archived: "0" } }).then(({ data }) => setItems(data.items)).catch(() => setItems([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-[#F5F3F0]/20 max-w-xl w-full p-6 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="jace-engine-picker">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display uppercase text-2xl">Uit de Engine.</div>
          <button onClick={onClose} className="w-8 h-8 border border-[#F5F3F0]/25 flex items-center justify-center" data-testid="jace-engine-picker-close"><X size={14}/></button>
        </div>
        {!choice ? (
          <>
            <div className="relative mb-3">
              <Search size={13} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#4A4A4A]" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek training…" className="editorial-input dark !pl-5" data-testid="jace-engine-picker-search" />
            </div>
            <div className="overflow-y-auto line-t line-dark flex-1" data-testid="jace-engine-picker-list">
              {items === null ? <div className="chip text-[#A3A3A3] py-4">Laden…</div>
                : items.length === 0 ? <div className="chip text-[#4A4A4A] py-4">Geen trainingen gevonden{q ? "" : " — maak er een in de Coaching Engine"}.</div>
                : items.map((it) => (
                  <button key={it.id} onClick={() => onSelect(it)} className="w-full text-left line-b line-dark py-3 flex items-center gap-3 hover:bg-white/[0.03] transition-colors" data-testid={`jace-engine-pick-${it.id}`}>
                    <span className="w-2.5 h-2.5 shrink-0" style={{ background: it.color || "#C6FF3C" }} />
                    <span className="flex-1">
                      <span className="block text-sm text-[#DDD6CB]">{it.name}</span>
                      <span className="chip text-[#4A4A4A]">{it.type} · {it.planned_duration} MIN · {it.version}{it.folder ? ` · ${it.folder}` : ""}</span>
                    </span>
                    {it.favorite ? <span style={{ color: JACE }} className="chip">★</span> : null}
                  </button>
                ))}
            </div>
          </>
        ) : (
          <div data-testid="jace-engine-picker-choice">
            <p className="text-[#DDD6CB] text-sm">
              <span style={{ color: JACE }}>{choice.name}</span> importeren in deze sessie. Je huidige sessie bevat al blokken — wat wil je doen?
            </p>
            <div className="flex flex-col gap-3 mt-6">
              <button onClick={() => { if (window.confirm("Alle huidige blokken en doelen worden vervangen. Doorgaan?")) onApply(choice, "replace"); }}
                className="chip px-4 py-3 border border-red-400/40 text-red-300 hover:bg-red-400/10 transition-colors text-left" data-testid="jace-engine-replace">
                VERVANG ALLES <span className="text-[#4A4A4A]">— huidige inhoud gaat weg (snapshot van de training)</span>
              </button>
              <button onClick={() => onApply(choice, "append")}
                className="chip px-4 py-3 border transition-colors text-left hover:text-[#C6FF3C]" style={{ borderColor: `${JACE}55` }} data-testid="jace-engine-append">
                TOEVOEGEN <span className="text-[#4A4A4A]">— blokken komen onder de huidige inhoud</span>
              </button>
              <button onClick={() => onSelect(null)} className="chip px-4 py-2 text-[#A3A3A3] text-left" data-testid="jace-engine-choice-back">← Andere training kiezen</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
